import React, { FC, ReactNode, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useOrganizationState } from '@/state';
import { MenuItemProps } from '@/types';

import {
  DockedHelpAiChat,
  HelpAiChatProvider,
  useHelpAiChat,
} from '../../views/Dashboard/components/HelpAiChat';
import { SessionIdleGuard } from '../SessionIdleGuard';
import { StripeTermsGate } from '../StripeTermsGate';
import {
  LayoutWrapper,
  Main,
  MainHeader,
  PageHeadline,
} from './Layout.styled';
import { MobileTrialBar } from './MobileTrialBar';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

const MAIN_CONTENT_ID = 'main-content';

interface LayoutProps {
    children: ReactNode;
    menuConfig? : MenuItemProps[];
}
// Aktywną (wybraną) organizację ładuje ProtectedRoute (loader do orgResolved).
// Lista dostępnych org (getOrganizationsByIds) ładuje się W TLE — jej loading/błąd
// obsługuje OrganizationSelector (sidebar + modal); NIE blokuje dashboardu.
// Wewnętrzny layout — czyta stan asystenta AI (`useHelpAiChat`), więc musi żyć
// wewnątrz `HelpAiChatProvider`. Gdy dok czatu jest otwarty, wpychamy całą ramę
// aplikacji w lewo (`$chatOpen` → padding-right na desktopie), a sidebar zwija
// się samodzielnie (czyta ten sam kontekst).
const LayoutInner: FC<LayoutProps> = ({
  children,
  menuConfig,
}) => {
  const [showMobileNavigation, setShowMobileNavigation] = useState(false);
  const location = useLocation();
  const { isOpen: isChatOpen } = useHelpAiChat();
  const { isDisabled } = useOrganizationState();

  useLayoutEffect(() => {
    setShowMobileNavigation(false);
    // Reset scroll on window (mobile)
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    // Reset scroll on Main container (desktop/tablet)
    const mainElement = document.getElementById(MAIN_CONTENT_ID);
    if (mainElement) {
      mainElement.scrollTop = 0;
    }
  }, [location.pathname]);

  return (
    <LayoutWrapper $isMenuOpen={showMobileNavigation} $chatOpen={isChatOpen}>
      <Sidebar
        menuConfig={menuConfig}
        showMobile={showMobileNavigation}
        setShowMobile={() => setShowMobileNavigation(!showMobileNavigation)}
      />
      <PageHeadline>
        <MainHeader>
          <TopBar showMobileNavigation={() => setShowMobileNavigation(!showMobileNavigation)} />
        </MainHeader>
      </PageHeadline>
      <Main id={MAIN_CONTENT_ID} $chatOpen={isChatOpen}>
        {children}
      </Main>
      <MobileTrialBar />
      {/* Czat AI nie ma sensu przy wyłączonej organizacji — panel i tak
          nie pokazuje treści, których asystent miałby dotyczyć. */}
      {!isDisabled && <DockedHelpAiChat />}
      <SessionIdleGuard />
      {/* Nowa wersja regulaminu płatności — prośba o akceptację zaraz po zalogowaniu.
          Komponent sam decyduje, czy się pokazać (tylko przy zmianie wersji). */}
      <StripeTermsGate />
    </LayoutWrapper>
  );
};

export const Layout: FC<LayoutProps> = (props) => (
  <HelpAiChatProvider>
    <LayoutInner {...props} />
  </HelpAiChatProvider>
);
